import { BetPosition } from '@/feature/bets-sbk/hooks/types';

// Polymarket US taker fee: fee = theta * contracts * price * (1 - price),
// rounded to the nearest cent with banker's rounding (https://docs.polymarket.us/fees).
// Users placing entries are always the taker, so maker rebates and volume tiers don't apply.
// Theta is configured per environment in the Predictions product document in Prismic; this is
// the fallback (the schedule effective September 16, 2026) for when the key is absent or invalid.
const DEFAULT_TAKER_FEE_THETA = 0.0695;

export const parseTakerFeeTheta = (value: string | undefined): number => {
    const theta = value?.trim() ? Number(value) : NaN;
    return theta >= 0 && theta < 1 ? theta : DEFAULT_TAKER_FEE_THETA;
};

export type PredictionPositionSummary = {
    fees: number;
    cost: number;
    payout: number;
    contracts: number;
};

// Round half to even ("banker's rounding") at cent precision, per the Polymarket US fee docs:
// exact half cents go to the nearest even cent ($0.025 -> $0.02, $0.035 -> $0.04).
export const roundToCents = (value: number) => {
    const cents = value * 100;
    const floor = Math.floor(cents);
    const diff = cents - floor;
    // Binary floats can't represent half cents exactly (0.035 * 100 = 3.5000000000000004,
    // 1.005 * 100 = 100.49999999999999), so an epsilon window around .5 catches drift in
    // both directions where a strict equality check would miss the half-cent case entirely.
    if (Math.abs(diff - 0.5) < 1e-9) {
        return (floor % 2 === 0 ? floor : floor + 1) / 100;
    }
    return Math.round(cents) / 100;
};

/**
 * Derives the position summary from the buy amount and the selection's decimal odds.
 * The buy amount is all-in: it covers the contract cost plus the taker fee, so
 * contracts solve `buy = C * p + theta * C * p * (1 - p)` with `p = 1 / odds`.
 * Contracts pay $1 each, so the payout equals the contract count.
 */
export const getEstimatedPositionSummary = (stake: number, odds: number, theta: number): PredictionPositionSummary => {
    if (stake <= 0 || odds <= 1) {
        return { fees: 0, cost: 0, payout: 0, contracts: 0 };
    }
    const price = 1 / odds;
    const contracts = stake / (price * (1 + theta * (1 - price)));
    const fees = roundToCents(theta * contracts * price * (1 - price));
    return {
        fees,
        cost: stake - fees,
        payout: contracts,
        contracts,
    };
};

export type ConfirmedPositionSummary = PredictionPositionSummary & {
    odds: number;
};

/**
 * Confirmation summary from a position fill: cost is `bet_amount`, fees are the exchange fee, and
 * contracts pay $1 each, so payout equals `quantity`. Returns null unless the fill is
 * a parseable BUY (placements are always buys; SELLs are cash-outs).
 */
export const getConfirmedPositionSummary = (position: BetPosition): ConfirmedPositionSummary | null => {
    if (position.side !== 'BUY') {
        return null;
    }
    const price = position.price;
    const cost = position.bet_amount;
    const quantity = position.quantity;
    if (!(price > 0) || price >= 1 || !(cost > 0) || !(quantity > 0)) {
        return null;
    }
    return { odds: 1 / price, cost, payout: quantity, contracts: quantity, fees: position.exchange_fee };
};
